import { motion, useScroll, useTransform } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import useMagneticEffect from '../hooks/useMagneticEffect';

const Hero = () => {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const buttonRef = useMagneticEffect(0.5);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Reduce particle count based on screen size for better performance
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 30 : 50; // Reduced from 80
    const connectionDistance = isMobile ? 80 : 100; // Reduced from 120

    const particles = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.4 + 0.2
      });
    }

    let animationFrame;
    let frameCount = 0;

    function animate() {
      frameCount++;
      // Skip every other frame on mobile for better performance
      if (isMobile && frameCount % 2 === 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Only draw connections for nearby particles (optimization)
        for (let j = i + 1; j < particles.length; j++) {
          const particle2 = particles[j];
          const dx = particle.x - particle2.x;
          const dy = particle.y - particle2.y;

          // Quick distance check before sqrt
          if (Math.abs(dx) < connectionDistance && Math.abs(dy) < connectionDistance) {
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < connectionDistance) {
              ctx.strokeStyle = `rgba(255, 87, 34, ${0.1 * (1 - distance / connectionDistance)})`;
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(particle2.x, particle2.y);
              ctx.stroke();
            }
          }
        }

        ctx.fillStyle = `rgba(255, 87, 34, ${particle.opacity})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrame = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const title1 = "A digital agency";
  const title2 = "focused on web.";

  return (
    <section ref={sectionRef} className="relative min-h-screen bg-[#0f0f0f] flex items-center justify-center overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 opacity-30 will-change-transform" />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent pointer-events-none" />

      {/* Animated grid - simplified */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none animate-grid"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,87,34,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,87,34,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}
      />

      <motion.div
        style={{ opacity, y }}
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-20"
      >
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.6, 0.05, 0.01, 0.9] }}
          className="text-orange-500 text-xs sm:text-sm md:text-base tracking-[0.3em] sm:tracking-[0.4em] mb-6 sm:mb-8 md:mb-12 font-light relative"
        >
          <span className="absolute -left-3 sm:-left-4 top-1/2 -translate-y-1/2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-500 rounded-full animate-pulse" />
          WE ARE RUKN ALFAHALAH TECHNOLOGY
        </motion.div>

        <div className="overflow-hidden mb-4 sm:mb-6 md:mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.6, 0.05, 0.01, 0.9] }}
            className="text-white text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight sm:leading-tight md:leading-tight will-change-transform"
          >
            {title1}
            <br />
            {title2}
          </motion.h1>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-gray-400 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mb-8 sm:mb-10 md:mb-12 leading-relaxed"
        >
          We are a creative team of designers, developers, strategists, and producers building elevated websites in the heart of Dubai.
        </motion.p>

        <motion.button
          ref={buttonRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="group relative flex items-center gap-2 sm:gap-3 bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full overflow-hidden transition-all duration-300 will-change-transform"
        >
          <span className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          <span className="relative z-10 group-hover:text-white transition-colors duration-300 text-xs sm:text-sm tracking-wider font-medium">
            GET TO KNOW US
          </span>
          <Plus size={16} className="relative z-10 group-hover:rotate-90 group-hover:text-white transition-all duration-300 sm:w-[18px] sm:h-[18px]" />
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="hidden sm:flex absolute right-4 sm:right-6 md:right-8 bottom-6 sm:bottom-8 flex-col items-center gap-3 sm:gap-4"
      >
        <div className="text-white text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] font-light" style={{ writingMode: 'vertical-rl' }}>
          SCROLL
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-[1px] h-12 sm:h-16 bg-gradient-to-b from-orange-500 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
